import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { useMatch } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import {
  FilterAccordion,
  StyledLinkBox,
} from '@sorare/core/src/atoms/layout/Accordion';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelS, LabelXS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import Avatar from '@sorare/core/src/components/user/Avatar';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { getCurrentSportClubName } from 'lib/getCurrentSportClubName';

import { PastLeaderboardList } from '../PastLeaderboardList';
import { CumulativeRanking_So5UserGroupMembership } from './__generated__/index.graphql';

const Wrapper = styled.div`
  --padding-inline-value: var(--triple-unit);
  position: relative;
  padding-inline: var(--padding-inline-value);
  background-color: var(--c-neutral-300);
`;

const Root = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns:
    var(--quadruple-unit) var(--quadruple-unit) 1fr
    calc(11 * var(--unit));
  gap: var(--double-unit);
  align-items: center;

  --c-row-background: transparent;

  & > * {
    &:last-child ${Horizontal} {
      padding-right: var(--intermediate-unit);
      justify-content: end;
    }
  }

  &:first-child {
    &::before {
      content: '';
      top: 0;
      bottom: 0;
      left: calc(-1 * var(--padding-inline-value));
      right: calc(-1 * var(--padding-inline-value));
      background-color: var(--c-row-background);
      position: absolute;
      z-index: -1;
      pointer-events: none;
    }
  }

  &.isMyRanking {
    &:first-child {
      &::before {
        --c-row-background: rgba(var(--c-rgb-brand-600), 1);
      }
    }
  }

  &.isFocusedRanking {
    &:first-child {
      &::before {
        --c-row-background: rgba(var(--c-rgb-brand-600), 0.75);
      }
    }
  }

  &:first-child:hover {
    &::before {
      --c-row-background: rgba(var(--c-rgb-brand-600), 0.75);
    }
  }
`;

const AvatarContainer = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  border-radius: var(--quadruple-unit);
  overflow: hidden;
  background-color: var(--c-neutral-500);

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Infos = styled(Vertical).attrs({ gap: 0 })`
  overflow: hidden;
`;

type Props = {
  so5Ranking: CumulativeRanking_So5UserGroupMembership;
  isMyRanking?: boolean;
  userGroupSlug: string;
  activeLineupId?: string;
  withCollapsibleRanking?: boolean;
  rankingLink?: (
    fixtureSlug: string,
    userGroupSlug: string,
    userSlug: string,
    eventType?: So5FixtureEvent
  ) => string;
};

export const CumulativeRanking = ({
  so5Ranking,
  isMyRanking,
  userGroupSlug,
  activeLineupId,
  withCollapsibleRanking,
  rankingLink,
}: Props) => {
  const { user } = so5Ranking;

  const rankingRoute =
    rankingLink?.(
      so5Ranking?.lastSo5Rankings[0]?.so5Fixture.slug ?? '',
      userGroupSlug,
      user.slug,
      so5Ranking?.lastSo5Rankings[0]?.so5Fixture.type
    ) ?? '';
  const matchRoute = useMatch(rankingRoute);
  const isFocusedRanking = Boolean(rankingRoute && matchRoute);

  const title = (
    <Root
      key={so5Ranking.id}
      className={classNames({ isMyRanking, isFocusedRanking })}
    >
      <BodyS as="p" bold>
        <FormattedNumber value={so5Ranking.ranking ?? 0} />
      </BodyS>
      <AvatarContainer>
        <Avatar user={user} />
      </AvatarContainer>
      <Infos gap={0}>
        <BodyS as="p" bold>
          <Ellipsis>{getCurrentSportClubName(user)}</Ellipsis>
        </BodyS>
        <LabelS as="p">
          <Ellipsis>{user.nickname}</Ellipsis>
        </LabelS>
      </Infos>
      <LabelXS as="p">
        <Horizontal gap={0.5} as="span">
          <FormattedMessage
            {...fantasy.pointsWithStyledScore}
            values={{
              score: so5Ranking.score,
              span: (...chunks) => (
                <BodyS as="span" bold>
                  {chunks}
                </BodyS>
              ),
            }}
          />
        </Horizontal>
      </LabelXS>
    </Root>
  );

  return (
    <Wrapper>
      {withCollapsibleRanking ? (
        <FilterAccordion noBorder noHorizontalPadding title={title}>
          {({ expanded }) => (
            <PastLeaderboardList
              expanded={expanded}
              userSlug={user.slug}
              userGroupSlug={userGroupSlug}
              activeLineupId={activeLineupId}
            />
          )}
        </FilterAccordion>
      ) : (
        <StyledLinkBox
          as={Link}
          $noHorizontalPadding
          $isFilter={false}
          to={
            rankingLink?.(
              so5Ranking?.lastSo5Rankings[0]?.so5Fixture.slug ?? '',
              userGroupSlug,
              user.slug,
              so5Ranking?.lastSo5Rankings[0]?.so5Fixture.type
            ) ?? ''
          }
        >
          {title}
        </StyledLinkBox>
      )}
    </Wrapper>
  );
};

CumulativeRanking.fragments = {
  So5UserGroupMembership: gql`
    fragment CumulativeRanking_So5UserGroupMembership on So5UserGroupMembership {
      id
      ranking
      score
      lastSo5Rankings {
        id
        so5Fixture {
          slug
          type
        }
      }
      user {
        id
        slug
        nickname
        ...Avatar_publicUserInfoInterface
        ...getCurrentSportClubName_PublicUserInfoInterface
      }
    }
    ${Avatar.fragments.publicUserInfoInterface}
    ${getCurrentSportClubName.fragments.PublicUserInfoInterface}
  ` as TypedDocumentNode<CumulativeRanking_So5UserGroupMembership>,
};
